/**
 * handle403Pivot — centralized 403 error.id dispatcher for system-tier errors.
 *
 * Called from the QueryClient cache onError callbacks (outside the React tree),
 * so it cannot use TanStack Router hooks. Navigation uses window.location.assign
 * for hard redirects (same pattern as handleSessionExpired) and showToast for
 * non-redirect cases.
 *
 * Anti-loop guard: checks window.location.pathname before pushing to avoid
 * re-triggering the same redirect when already on the target page.
 *
 * i18n: messages are inlined in two locales (same approach as handleSessionExpired)
 * because use-intl's useTranslations is not available outside the React tree.
 *
 * 8 handled error.id slugs (A2/A3 BE):
 *   account_pending_approval  → /pending-approval
 *   account_suspended         → /account-suspended
 *   org_pending_approval      → toast.warn + /select-org
 *   org_suspended             → toast.error + /select-org
 *   org_awaiting_approval     → /awaiting-approval
 *   feature_not_enabled       → toast.error (with feature name from detail)
 *   forbidden_root_only       → toast.error
 *   forbidden_system_admin_only → toast.error
 */

import { showToast } from "@/lib/toast";
import { getLocaleFromCookie } from "@/i18n/locale-cookie";

type Locale = "en" | "vi";

// Localised strings inlined — avoids use-intl dependency outside React tree.
// Technical terms (API, URL, OAuth, etc.) kept verbatim in VN per CLAUDE.md.
const MESSAGES: Record<
  string,
  Record<Locale, { title: string; description?: string }>
> = {
  account_pending_approval: {
    en: { title: "Account pending approval", description: "Your account is awaiting admin approval." },
    vi: { title: "Tài khoản đang chờ phê duyệt", description: "Tài khoản của bạn đang chờ admin phê duyệt." },
  },
  account_suspended: {
    en: { title: "Account suspended", description: "Your account has been suspended. Contact support for assistance." },
    vi: { title: "Tài khoản bị tạm khóa", description: "Tài khoản của bạn đã bị tạm khóa. Liên hệ hỗ trợ để được giúp đỡ." },
  },
  org_pending_approval: {
    en: { title: "Organization pending approval", description: "This organization is awaiting admin approval." },
    vi: { title: "Tổ chức đang chờ phê duyệt", description: "Tổ chức này đang chờ admin phê duyệt." },
  },
  org_suspended: {
    en: { title: "Organization suspended", description: "This organization has been suspended." },
    vi: { title: "Tổ chức bị tạm khóa", description: "Tổ chức này đã bị tạm khóa." },
  },
  org_awaiting_approval: {
    en: { title: "Organization awaiting approval", description: "Your organization is awaiting system-admin approval." },
    vi: { title: "Tổ chức đang chờ được duyệt", description: "Tổ chức của bạn đang chờ system admin phê duyệt." },
  },
  forbidden_root_only: {
    en: { title: "Root admin required", description: "This action requires system root privileges." },
    vi: { title: "Cần quyền root admin", description: "Thao tác này yêu cầu quyền system root." },
  },
  forbidden_system_admin_only: {
    en: { title: "System admin required", description: "This action requires system admin privileges." },
    vi: { title: "Cần quyền system admin", description: "Thao tác này yêu cầu quyền system admin." },
  },
};

interface ApiErrorWithDetail {
  id?: string;
  statusCode?: number;
  detail?: {
    feature?: string;
    available?: boolean;
  };
}

// Toast debounce window. When a page mounts N parallel useQuery calls and the
// BE returns the same 403 to all of them, naive dispatch would stack N toasts.
// First call per slug fires the toast; subsequent calls within the window are
// silenced. Window is short enough that the next real failure (e.g. retry on
// a fresh page) is not suppressed.
const TOAST_DEBOUNCE_MS = 1500;
const lastToastAt: Map<string, number> = new Map();
function shouldFireToast(slug: string): boolean {
  const now = Date.now();
  const last = lastToastAt.get(slug) ?? 0;
  if (now - last < TOAST_DEBOUNCE_MS) return false;
  lastToastAt.set(slug, now);
  return true;
}

/**
 * Reset the toast debounce state. Test-only — production code never needs
 * this since the debounce window is intentionally short. Exported so test
 * `beforeEach` can clear stale state between cases.
 */
export function resetToastDebounceForTests(): void {
  lastToastAt.clear();
}

/** Returns true if the current page is already at the given pathname. */
function isAlreadyAt(pathname: string): boolean {
  if (typeof window === "undefined") return false;
  return window.location.pathname === pathname;
}

/** Hard-navigate to pathname unless already there (anti-loop guard). */
function navigateTo(pathname: string): void {
  if (typeof window === "undefined") return;
  if (isAlreadyAt(pathname)) return;
  window.location.assign(pathname);
}

/**
 * Dispatch a 403 error by its error.id slug.
 *
 * Returns true if the error was handled (caller should suppress generic toast),
 * false if the slug is not recognised here (caller falls through to default).
 */
export function handle403Pivot(error: ApiErrorWithDetail): boolean {
  const locale: Locale = getLocaleFromCookie() === "vi" ? "vi" : "en";
  const id = error.id;

  switch (id) {
    case "account_pending_approval": {
      navigateTo("/pending-approval");
      return true;
    }

    case "account_suspended": {
      navigateTo("/account-suspended");
      return true;
    }

    case "org_pending_approval": {
      if (shouldFireToast(id)) {
        const msg = MESSAGES.org_pending_approval[locale];
        showToast.warning({ title: msg.title, description: msg.description });
      }
      navigateTo("/select-org");
      return true;
    }

    case "org_suspended": {
      if (shouldFireToast(id)) {
        const msg = MESSAGES.org_suspended[locale];
        showToast.error({ title: msg.title, description: msg.description });
      }
      navigateTo("/select-org");
      return true;
    }

    case "org_awaiting_approval": {
      navigateTo("/awaiting-approval");
      return true;
    }

    case "feature_not_enabled": {
      // Include feature name in toast when available; keep message useful even
      // when detail is absent (graceful degradation). Debounce key includes
      // the feature name so two distinct missing-feature errors on one page
      // still produce two toasts.
      const featureName = error.detail?.feature;
      const debounceKey = featureName ? `${id}:${featureName}` : id;
      if (shouldFireToast(debounceKey)) {
        const title =
          locale === "vi"
            ? featureName
              ? `Tính năng "${featureName}" chưa được bật`
              : "Tính năng chưa được bật"
            : featureName
              ? `Feature "${featureName}" is not enabled`
              : "Feature is not enabled";
        const description =
          locale === "vi"
            ? "Liên hệ admin tổ chức để bật tính năng này."
            : "Contact your organization admin to enable this feature.";
        showToast.error({ title, description });
      }
      return true;
    }

    case "forbidden_root_only": {
      if (shouldFireToast(id)) {
        const msg = MESSAGES.forbidden_root_only[locale];
        showToast.error({ title: msg.title, description: msg.description });
      }
      return true;
    }

    case "forbidden_system_admin_only": {
      if (shouldFireToast(id)) {
        const msg = MESSAGES.forbidden_system_admin_only[locale];
        showToast.error({ title: msg.title, description: msg.description });
      }
      return true;
    }

    default:
      return false;
  }
}
